#!/usr/bin/env node

/**
 * `npx @productbrain/mcp@beta setup`
 *
 * Guided onboarding: get API key from the app, paste it, write MCP config,
 * and optionally install Cursor rules/skills (additive-only).
 */

import { execSync } from "node:child_process";
import { createInterface } from "node:readline";
import { existsSync, writeFileSync, mkdirSync } from "node:fs";
import { join } from "node:path";
import { resolveClient, writeClientConfig, MCP_NPX_PACKAGE, type McpClientInfo } from "./config-writer.js";
import { initAnalytics, trackSetupStarted, trackSetupCompleted, shutdownAnalytics } from "../analytics.js";
import { DEFAULT_CLOUD_URL } from "../client.js";
import { warnOnProdFallthrough } from "../prod-fallthrough.js";

const APP_URL =
  process.env.PRODUCTBRAIN_APP_URL ?? "https://work.productbrain.io";

// ── Helpers ─────────────────────────────────────────────────────────────

function bold(s: string) {
  return `\x1b[1m${s}\x1b[0m`;
}
function green(s: string) {
  return `\x1b[32m${s}\x1b[0m`;
}
function dim(s: string) {
  return `\x1b[2m${s}\x1b[0m`;
}
function orange(s: string) {
  return `\x1b[33m${s}\x1b[0m`;
}

function log(msg: string) {
  process.stdout.write(`${msg}\n`);
}

function openBrowser(url: string) {
  const platform = process.platform;
  try {
    if (platform === "darwin") execSync(`open "${url}"`);
    else if (platform === "win32") execSync(`start "" "${url}"`);
    else execSync(`xdg-open "${url}"`);
  } catch {
    log(dim(`  Could not open browser automatically.`));
    log(`  Open this URL manually: ${url}`);
  }
}

function prompt(question: string): Promise<string> {
  return new Promise((resolve) => {
    const rl = createInterface({ input: process.stdin, output: process.stdout });
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

function promptChoice(question: string, choices: string[]): Promise<number> {
  return new Promise((resolve) => {
    log("");
    log(bold(question));
    choices.forEach((c, i) => log(`  ${i + 1}) ${c}`));
    const rl = createInterface({ input: process.stdin, output: process.stdout });
    rl.question(`\n  ${dim("Choice [1]:")} `, (line) => {
      rl.close();
      const n = parseInt(line.trim(), 10);
      if (isNaN(n) || n < 1 || n > choices.length) {
        resolve(0);
      } else {
        resolve(n - 1);
      }
    });
  });
}

// ── Workspace Verification ───────────────────────────────────────────────

/**
 * Resolve the gateway URL the setup flow probes. TEN-2382: setup is an
 * independent entry point (no bootstrap), so it warns on silent prod
 * fall-through here. DEFAULT_CLOUD_URL is imported from client.ts (the
 * in-package SSOT) — no local re-declare.
 */
export function resolveSetupSiteUrl(): string {
  const explicit = process.env.CONVEX_SITE_URL ?? process.env.PRODUCTBRAIN_URL;
  const url = explicit ?? DEFAULT_CLOUD_URL;
  warnOnProdFallthrough(url, { explicit: explicit != null });
  return url;
}

async function verifyWorkspace(
  apiKey: string,
): Promise<{ name: string; slug: string } | null> {
  const siteUrl = resolveSetupSiteUrl();

  try {
    const res = await fetch(`${siteUrl.replace(/\/$/, "")}/api/aki`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({ fn: "resolveWorkspace", args: {} }),
    });

    if (!res.ok) return null;
    const json = (await res.json()) as {
      data?: { name: string; slug: string } | null;
      error?: string;
    };
    return json.data ?? null;
  } catch {
    return null;
  }
}

// ── Main ────────────────────────────────────────────────────────────────

export async function runSetup() {
  initAnalytics();
  trackSetupStarted();

  log("");
  log(bold(`  Product${orange("Brain")} Setup`));
  log(dim("  Connect your AI assistant to your chain\n"));

  const apiKeysUrl = `${APP_URL}/settings/api-keys`;

  log(`  ${dim("1. Get your API key from Settings → API Keys")}`);
  log(`     ${dim(apiKeysUrl)}\n`);

  const openNow = await prompt(`  Open this URL in your browser? [Y/n]: `);
  if (openNow.toLowerCase() !== "n" && openNow.toLowerCase() !== "no") {
    openBrowser(apiKeysUrl);
  }

  log("");
  log(`  ${dim("2. Generate a key (if you don't have one), then copy it.\n")}`);

  const apiKey = await prompt(`  Paste your API key (pb_sk_...): `);

  if (!apiKey || !apiKey.startsWith("pb_sk_")) {
    log(`  ${orange("!")} Invalid key format. Keys start with pb_sk_.`);
    log(`  Get one at ${apiKeysUrl}\n`);
    await shutdownAnalytics();
    process.exit(1);
  }

  log(`  ${green("✓")} Key received`);

  const workspace = await verifyWorkspace(apiKey);
  if (workspace) {
    log(`  ${green("✓")} Connected to workspace: ${bold(workspace.name)} ${dim(`(${workspace.slug})`)}`);
  } else {
    log(`  ${orange("!")} Could not verify workspace. Check your key at ${apiKeysUrl}`);
  }
  log("");

  const CLIENT_NAMES = ["Cursor", "Claude Desktop"] as const;
  const options = [...CLIENT_NAMES, "Other"];

  const choice = await promptChoice("Where do you want to set up Product Brain?", options);

  if (choice === 2) {
    printConfigSnippet(apiKey);
    trackSetupCompleted("Other", "snippet_shown");
  } else {
    const client = resolveClient(CLIENT_NAMES[choice]);
    if (client) {
      const outcome = await writeConfig(client, apiKey);
      trackSetupCompleted(CLIENT_NAMES[choice], outcome);
    } else {
      log(`  ${orange("!")} ${CLIENT_NAMES[choice]} config path not available on this platform.`);
      printConfigSnippet(apiKey);
      trackSetupCompleted(CLIENT_NAMES[choice], "write_error");
    }
  }

  // Cursor-specific: offer to install rule (additive-only)
  if (choice === 0) {
    await offerCursorRulesInstall();
    printDeeplink(apiKey);
  }

  // Claude-specific: print snippet (never write to CLAUDE.md)
  if (choice === 1) {
    printClaudeSnippet();
  }

  log("");
  log(
    `  ${green("✓")} Done! Restart your AI assistant and try: ${bold('"Start PB"')}`,
  );
  printHelpLink();
  await shutdownAnalytics();
}

async function writeConfig(
  client: McpClientInfo,
  apiKey: string,
): Promise<"config_written" | "config_existed" | "write_error"> {
  try {
    const wrote = await writeClientConfig(client, apiKey);
    if (wrote) {
      log(`  ${green("✓")} Wrote config to ${dim(client.configPath)}`);
      return "config_written";
    } else {
      log(`  ${dim("ℹ")} ${client.name} already configured — skipped`);
      return "config_existed";
    }
  } catch (err: any) {
    log(`  ${orange("!")} Could not write ${client.name} config: ${err.message}`);
    printConfigSnippet(apiKey);
    return "write_error";
  }
}

function printHelpLink() {
  log(`  ${dim(`Need help? See ${APP_URL}/settings/api-keys`)}`);
  log("");
}

function printConfigSnippet(apiKey: string) {
  log("");
  log(bold("  Add this to your MCP client config:\n"));
  const snippet = JSON.stringify(
    {
      mcpServers: {
        "Product Brain": {
          command: "npx",
          args: ["-y", MCP_NPX_PACKAGE],
          env: { PRODUCTBRAIN_API_KEY: apiKey },
        },
      },
    },
    null,
    2,
  );
  for (const line of snippet.split("\n")) {
    log(`    ${line}`);
  }
  log("");
}

// ── Cursor Rules/Skills Install (additive-only) ─────────────────────────

const CURSOR_RULE_FILENAME = "product-brain.mdc";

const CURSOR_RULE_CONTENT = `---
description: Product Brain MCP — single source of truth for product knowledge
globs:
alwaysApply: true
---

# Product Brain MCP

Product Brain is your product knowledge base. The Chain is the single source of truth.

Every entry is either a **draft** (captured but not accepted) or **accepted** (on the Chain, SSOT).
Accepting onto the Chain is the compounding act.

## Quick Start

Say **"Start PB"** or **"Start Product Brain"** to begin. This single call:
- Orients you to the workspace (readiness, gaps, planned work)
- Unlocks write tools for the session
- Surfaces your next recommended action

## Tool Workflow

1. **Start here**: \`orient action=start\` — workspace context + next action
2. **Search**: \`entries action=search\` — find entries across all collections
3. **Drill in**: \`entries action=get\` — full record with data, labels, relations
4. **Context**: \`context action=gather\` — related knowledge around an entry or task
5. **Capture**: \`capture\` — create knowledge with auto-linking + quality score
6. **Accept**: \`entries action=commit\` — promote drafts to SSOT when confirmation is still required
7. **Connect**: \`relations action=suggest\` then \`relations action=create\` to build the graph

## Bulk Knowledge Input

When given a document or batch of knowledge to capture:
1. Scan the input — identify all collections needed
2. Call \`collections action=list\` — compare against what exists
3. Propose missing collections to the user for confirmation
4. Ask a platform admin to provision each confirmed collection (creating one is no longer
   self-serve — ask an admin, or enable the module ontology pack that owns the drawer)
5. Then capture entries into the correct collections

Never stuff entries into the wrong collection. Never silently skip knowledge.

## Rules

- In Open mode, user-authored captures can be accepted immediately unless the user asks to keep them as drafts.
- In consensus/role modes, only call \`entries action=commit\` when the user confirms.
- Use \`relations action=suggest\` after capturing to discover and create relations.
- Collections are dynamic, but creating a new one is not self-serve — ask a platform admin to
  provision it, or enable the module ontology pack that owns the drawer.
- When lost, fetch \`productbrain://orientation\` for the full system map.
`;

function isCursorProject(): boolean {
  return existsSync(join(process.cwd(), ".cursor")) || existsSync(join(process.cwd(), ".cursorignore"));
}

async function offerCursorRulesInstall(): Promise<void> {
  if (!isCursorProject()) return;

  const answer = await prompt(`\n  Install Product Brain rule for Cursor? [Y/n]: `);
  if (answer.toLowerCase() === "n" || answer.toLowerCase() === "no") {
    log(dim("  Skipped rule install."));
    return;
  }

  const rulesDir = join(process.cwd(), ".cursor", "rules");
  const rulePath = join(rulesDir, CURSOR_RULE_FILENAME);

  if (existsSync(rulePath)) {
    log(`  ${dim("ℹ")} Rule already exists at ${dim(rulePath)} — skipped`);
    return;
  }

  if (!existsSync(rulesDir)) {
    mkdirSync(rulesDir, { recursive: true });
  }

  writeFileSync(rulePath, CURSOR_RULE_CONTENT, "utf-8");
  log(`  ${green("✓")} Installed rule at ${dim(rulePath)}`);
}

function buildDeeplink(apiKey: string): string {
  const config = JSON.stringify({
    command: "npx",
    args: ["-y", MCP_NPX_PACKAGE],
    env: { PRODUCTBRAIN_API_KEY: apiKey },
  });
  const encoded = Buffer.from(config).toString("base64url");
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent("Product Brain")}&config=${encoded}`;
}

function printDeeplink(apiKey: string): void {
  const link = buildDeeplink(apiKey);
  log("");
  log(`  ${dim("One-click install for Cursor (paste in browser):")}`);
  log(`  ${link}`);
}

function printClaudeSnippet(): void {
  log("");
  log(bold("  For Claude Code / CLAUDE.md:"));
  log(dim("  Add this line to your ~/.claude/CLAUDE.md:"));
  log("");
  log(`    When Product Brain MCP is available, say "Start PB" at the beginning`);
  log(`    of each session to orient to the workspace and unlock write tools.`);
  log(`    In Open mode, user-authored captures can be accepted immediately unless the user asks to keep drafts.`);
  log(`    In consensus/role modes, only accept when the user confirms.`);
  log("");
}
